import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, TriangleAlert, BookOpen, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, type CurriculumSwapPreview } from '@/lib/api/admin';
import { curriculumApi } from '@/lib/api/curriculum';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type SourceKind = 'batch' | 'template';

/** Rows in the "what this destroys" panel. Zero-valued rows are hidden. */
function lossRows(p: CurriculumSwapPreview) {
  return [
    { label: 'Modules', value: p.modules },
    { label: 'Topics', value: p.topics },
    { label: 'Sessions', value: p.sessions },
    { label: 'Resources', value: p.resources },
    { label: 'Assessments', value: p.assessments },
    { label: 'Quiz questions', value: p.mcq_questions },
    { label: 'Visualizations', value: p.visualizations },
    { label: 'Students with progress', value: p.students_with_progress },
    { label: 'Progress records', value: p.progress_rows },
    { label: 'Submissions', value: p.submissions },
    { label: 'Discussion messages', value: p.messages },
    { label: 'Student notes', value: p.notes },
  ].filter((r) => r.value > 0);
}

export function ChangeCurriculumDialog({
  batchId,
  batchName,
  onClose,
  onSwapped,
}: {
  batchId: string;
  batchName: string;
  onClose: () => void;
  onSwapped: () => void;
}) {
  const [sourceKind, setSourceKind] = useState<SourceKind>('batch');
  const [sourceId, setSourceId] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { data: preview, isLoading: previewLoading } = useQuery({
    queryKey: ['admin', 'curriculum-swap-preview', batchId],
    queryFn: () => adminApi.getCurriculumSwapPreview(batchId),
  });

  const { data: sourceBatches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ['admin', 'curriculum-sources'],
    queryFn: () => adminApi.listCurriculumSourceBatches(),
    enabled: sourceKind === 'batch',
  });

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['curriculum', 'templates'],
    queryFn: async () => {
      const { data } = await curriculumApi.listTemplates();
      return (data ?? []).filter((t) => t.status === 'published');
    },
    enabled: sourceKind === 'template',
  });

  // Switching source kind invalidates the chosen source.
  useEffect(() => {
    setSourceId('');
    setError('');
  }, [sourceKind]);

  const options = useMemo(() => {
    if (sourceKind === 'batch') {
      return sourceBatches
        .filter((b) => b.batch_id !== batchId)
        .map((b) => ({
          id: b.batch_id,
          title: b.name,
          subtitle: b.org_name,
          detail: `${b.modules} modules · ${b.topics} topics · ${b.assessments} assessments${
            b.visualizations > 0 ? ` · ${b.visualizations} visualizations` : ''
          }`,
        }));
    }
    return templates.map((t) => ({
      id: t.id,
      title: t.title,
      subtitle: '',
      detail: `${t.module_count} modules · ${t.topic_count} topics`,
    }));
  }, [sourceKind, sourceBatches, templates, batchId]);

  const selectedOption = options.find((o) => o.id === sourceId);

  const losses = preview ? lossRows(preview) : [];
  const confirmed = confirmName.trim() === batchName.trim();
  const optionsLoading = sourceKind === 'batch' ? batchesLoading : templatesLoading;

  async function handleSubmit() {
    if (!sourceId || !confirmed || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await adminApi.swapBatchCurriculum(batchId, {
        source_kind: sourceKind,
        source_id: sourceId,
      });
      const c = result.copied;
      const parts = [`${c.modules} modules`, `${c.topics} topics`];
      if (c.assessments > 0) parts.push(`${c.assessments} assessments`);
      if (c.mcq_questions > 0) parts.push(`${c.mcq_questions} quiz questions`);
      if (c.visualizations > 0) parts.push(`${c.visualizations} visualizations`);
      toast.success(`Curriculum replaced — copied ${parts.join(' · ')}`);
      onSwapped();
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to change curriculum');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !submitting) onClose(); }}>
      <DialogContent className="max-w-lg p-0" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader className="border-b border-border px-5 py-4">
          <DialogTitle className="text-[15px]">Change curriculum</DialogTitle>
          <DialogDescription className="text-[11px]">
            Replaces this batch's curriculum. Students stay enrolled.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto px-5 py-5">
          {/* Source kind */}
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-foreground">Copy from</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSourceKind('batch')}
                className={`flex items-start gap-2 rounded-md border px-3 py-2.5 text-left transition-colors ${
                  sourceKind === 'batch'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-muted'
                }`}
              >
                <Layers className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <span>
                  <span className="block text-[13px] font-medium text-foreground">Existing batch</span>
                  <span className="block text-[11px] text-muted-foreground">
                    Full copy — sessions, resources, assessments, quizzes, visualizations
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSourceKind('template')}
                className={`flex items-start gap-2 rounded-md border px-3 py-2.5 text-left transition-colors ${
                  sourceKind === 'template'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-muted'
                }`}
              >
                <BookOpen className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <span>
                  <span className="block text-[13px] font-medium text-foreground">Template</span>
                  <span className="block text-[11px] text-muted-foreground">
                    Structure and lesson content only
                  </span>
                </span>
              </button>
            </div>
          </div>

          {/* Source picker */}
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-foreground">
              {sourceKind === 'batch' ? 'Source batch' : 'Curriculum template'}
            </label>
            {optionsLoading ? (
              <div className="flex h-9 items-center gap-2 rounded-md border border-border px-3">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                <span className="text-[12px] text-muted-foreground">Loading…</span>
              </div>
            ) : options.length === 0 ? (
              <p className="rounded-md border border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                {sourceKind === 'batch'
                  ? 'No other batch has a curriculum to copy.'
                  : 'No published templates. Publish a template first.'}
              </p>
            ) : (
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={sourceKind === 'batch' ? 'Select a batch…' : 'Select a template…'}
                  />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {options.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.title}
                      {o.subtitle ? ` — ${o.subtitle}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {selectedOption && (
              <p className="mt-1.5 text-[11px] text-muted-foreground">{selectedOption.detail}</p>
            )}
          </div>

          {/* What this destroys */}
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
            <div className="flex items-start gap-2">
              <TriangleAlert className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-destructive" />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-foreground">
                  This permanently deletes the current curriculum
                </p>
                {previewLoading ? (
                  <p className="mt-1 text-[11px] text-muted-foreground">Checking what's attached…</p>
                ) : !preview?.has_curriculum ? (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    This batch has no curriculum yet — nothing will be deleted.
                  </p>
                ) : losses.length === 0 ? (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    The current curriculum
                    {preview.current_template_title ? ` (${preview.current_template_title})` : ''} is empty.
                  </p>
                ) : (
                  <>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {preview.current_template_title
                        ? `Currently: ${preview.current_template_title}. `
                        : ''}
                      Cannot be undone.
                    </p>
                    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5">
                      {losses.map((r) => (
                        <div key={r.label} className="flex items-baseline justify-between gap-2">
                          <dt className="text-[11px] text-muted-foreground">{r.label}</dt>
                          <dd className="text-[11px] font-medium tabular-nums text-foreground">{r.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Typed confirmation */}
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-foreground">
              Type <span className="font-semibold">{batchName}</span> to confirm
            </label>
            <Input
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={batchName}
              autoComplete="off"
            />
          </div>

          {error && <p className="text-[12px] text-destructive">{error}</p>}
        </div>

        <DialogFooter className="border-t border-border px-5 py-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleSubmit}
            disabled={!sourceId || !confirmed || submitting}
          >
            {submitting ? (
              <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Replacing…</>
            ) : (
              'Replace curriculum'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
